import { useState } from "react";
import { toast } from "sonner";
import { Upload, AlertCircle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, fmtMoney, fmtNum, yesterday } from "@/lib/api";

export async function downloadModele() {
  try {
    const r = await api.get("/tfj/modele", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modele_tfj_kgd.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Téléchargement du modèle impossible");
  }
}

const ACTS = [["loto", "Loto"], ["hippique", "Hippique"], ["sport", "Sport"]];

function Preview({ p, errorsOnly }) {
  const rows = errorsOnly ? p.rows.filter((r) => r.errors.length || r.warnings.length) : p.rows;
  return (
    <div className="border rounded-md overflow-auto max-h-[45vh]">
      <table className="w-full text-xs" data-testid="import-preview-table">
        <thead className="bg-muted/60 sticky top-0">
          <tr>
            <th className="p-2 text-left">Ligne</th><th className="p-2 text-left">TPE</th><th className="p-2 text-left">Revendeur / superviseur</th>
            {ACTS.map(([a, l]) => <th key={a} className="p-2 text-right whitespace-nowrap">{l} CA / Gains</th>)}
            <th className="p-2 text-right">Solde</th><th className="p-2 text-left">Contrôles</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.ligne} className={`border-t ${r.errors.length ? "bg-red-50" : r.warnings.length ? "bg-amber-50" : ""}`}>
              <td className="p-2 font-mono">{r.ligne}</td>
              <td className="p-2 font-mono font-semibold">{r.identifiant || "—"}</td>
              <td className="p-2">{r.revendeur_label || "—"}<div className="text-muted-foreground">{r.superviseur_label}</div></td>
              {ACTS.map(([a]) => <td key={a} className="p-2 text-right font-mono whitespace-nowrap">{fmtNum(r[`${a}_ca`])} / {fmtNum(r[`${a}_gains`])}</td>)}
              <td className={`p-2 text-right font-mono ${r.total_solde < 0 ? "text-primary" : ""}`}>{fmtNum(r.total_solde)}</td>
              <td className="p-2">
                {r.errors.map((e) => <div key={e} className="text-red-700 flex gap-1"><AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />{e}</div>)}
                {r.warnings.map((e) => <div key={e} className="text-amber-800">{e}</div>)}
                {!r.errors.length && !r.warnings.length && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ImportDialog({ open, onOpenChange, defaultDate, onDone }) {
  const [date, setDate] = useState(defaultDate || yesterday());
  const [file, setFile] = useState(null);
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("ajouter");
  const [errorsOnly, setErrorsOnly] = useState(false);

  const reset = () => { setP(null); setFile(null); setMode("ajouter"); };
  const analyse = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("date", date);
    setBusy(true);
    try {
      const r = await api.post("/tfj/import/apercu", fd);
      setP(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tfj/import/${p.id}/confirmer`, { mode });
      toast.success(`${r.data.importees} ligne(s) importée(s)`);
      reset();
      onOpenChange(false);
      onDone?.(r.data.journee_id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const importables = p ? p.stats.valides - (mode === "ajouter" ? p.stats.deja_importes : 0) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="tfj-import-dialog">
        <DialogHeader>
          <DialogTitle>Importer les résultats d'une journée</DialogTitle>
          <DialogDescription>Fichier Excel : une ligne par TPE, CA et gains pour Loto, Hippique et Sport. Solde = CA − Gains.</DialogDescription>
        </DialogHeader>
        {!p ? (
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Journée</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="import-date-input" /></div>
            <div><Label>Fichier Excel (.xlsx)</Label><Input type="file" accept=".xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" /></div>
            <div className="sm:col-span-2 flex justify-between items-center">
              <Button variant="link" className="px-0" onClick={downloadModele} data-testid="import-download-template">Télécharger le modèle Excel</Button>
              <Button onClick={analyse} disabled={!file || busy} data-testid="import-analyse-button"><Upload className="h-4 w-4 mr-1" />{busy ? "Analyse…" : "Analyser le fichier"}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 text-xs" data-testid="import-stats">
              <span className="px-2 py-1 rounded bg-muted">Journée {p.date}</span>
              <span className="px-2 py-1 rounded bg-muted">{p.stats.total} ligne(s)</span>
              <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-800">{p.stats.valides} valide(s)</span>
              <span className="px-2 py-1 rounded bg-red-50 text-red-700">{p.stats.erreurs} en erreur (ignorées)</span>
              {p.stats.deja_importes > 0 && <span className="px-2 py-1 rounded bg-amber-50 text-amber-800">{p.stats.deja_importes} déjà importée(s)</span>}
              <span className="px-2 py-1 rounded bg-accent">CA {fmtMoney(p.totaux.total_ca)} · Gains {fmtMoney(p.totaux.total_gains)} · Solde {fmtMoney(p.totaux.total_solde)}</span>
            </div>
            <div className="text-[11px] text-muted-foreground">Colonnes reconnues : {Object.entries(p.colonnes).map(([k, v]) => `${v} → ${k}`).join(" · ")}</div>
            <div className="flex items-center gap-2 text-xs"><Switch checked={errorsOnly} onCheckedChange={setErrorsOnly} data-testid="import-errors-only" />Afficher uniquement les lignes avec anomalies</div>
            <Preview p={p} errorsOnly={errorsOnly} />
            {p.stats.deja_importes > 0 && (
              <div className="flex gap-4 text-sm" data-testid="import-mode">
                <label className="flex items-center gap-2"><input type="radio" checked={mode === "ajouter"} onChange={() => setMode("ajouter")} />Ignorer les TPE déjà importés</label>
                <label className="flex items-center gap-2"><input type="radio" checked={mode === "remplacer"} onChange={() => setMode("remplacer")} data-testid="import-mode-remplacer" />Remplacer les lignes existantes</label>
              </div>
            )}
          </div>
        )}
        {p && (
          <DialogFooter>
            <Button variant="outline" onClick={reset}>Changer de fichier</Button>
            <Button onClick={confirm} disabled={busy || importables <= 0} data-testid="import-confirm-button">Confirmer l'import ({importables} ligne(s))</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
